import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,

  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useSettingsStore } from '../../stores/settingsStore';

interface Shortcut {
  action: string;
  keys: string[];
  category: string;
}

const KeyboardShortcuts: React.FC = () => {
  const { settings, isLoading, isSaving, updateSettings } = useSettingsStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [snackbar, setSnackbar] = React.useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // SettingsLayout already fetches settings

  const handleToggle = async (value: boolean) => {
    try {
      await updateSettings({ enableKeyboardShortcuts: value });
      setSnackbar({ open: true, message: 'Settings saved', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to save settings', severity: 'error' });
    }
  };

  const shortcuts: Shortcut[] = [
    { action: 'Go to Inbox', keys: ['g', 'i'], category: 'Navigation' },
    { action: 'Go to Drafts', keys: ['g', 'd'], category: 'Navigation' },
    { action: 'Go to Sent', keys: ['g', 's'], category: 'Navigation' },
    { action: 'Compose new mail', keys: ['c'], category: 'Compose' },
    { action: 'Reply', keys: ['r'], category: 'Compose' },
    { action: 'Reply all', keys: ['a'], category: 'Compose' },
    { action: 'Forward', keys: ['f'], category: 'Compose' },
    { action: 'Send email', keys: ['Ctrl', 'Enter'], category: 'Compose' },
    { action: 'Delete', keys: ['#'], category: 'Actions' },
    { action: 'Archive', keys: ['e'], category: 'Actions' },
    { action: 'Mark as read', keys: ['Shift', 'i'], category: 'Actions' },
    { action: 'Mark as unread', keys: ['Shift', 'u'], category: 'Actions' },
    { action: 'Star/Unstar', keys: ['s'], category: 'Actions' },
    { action: 'Search mail', keys: ['/'], category: 'Search' },
  ];

  const filteredShortcuts = shortcuts.filter(
    (shortcut) =>
      shortcut.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
      shortcut.keys.some((key) => key.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const groupedShortcuts = filteredShortcuts.reduce((acc, shortcut) => {
    if (!acc[shortcut.category]) {
      acc[shortcut.category] = [];
    }
    acc[shortcut.category].push(shortcut);
    return acc;
  }, {} as Record<string, Shortcut[]>);

  const enabled = settings.enableKeyboardShortcuts ?? true;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress size={32} color="primary" />
      </Box>
    );
  }

  return (
    <Box>
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={2000} 
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: '8px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Typography variant="h5">Keyboard Shortcuts</Typography>
        {isSaving && <CircularProgress size={16} color="primary" />}
      </Box>

      {/* Enable Toggle */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => handleToggle(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Enable keyboard shortcuts</Typography>
              <Typography variant="caption">Use keyboard shortcuts to navigate and perform actions</Typography>
            </Box>
          }
        />
      </Paper>

      {enabled && (
        <>
          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search shortcuts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ 
              mb: 3, 
              maxWidth: 350,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'divider' },
                '&:hover fieldset': { borderColor: 'primary.main' },
              },
            }}
          />

          {/* Shortcuts Table */}
          {Object.entries(groupedShortcuts).map(([category, categoryShortcuts]) => (
            <Paper key={category} variant="outlined" sx={{ mb: 2 }}>
              <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                  {category}
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {categoryShortcuts.map((shortcut, index) => (
                      <TableRow key={index} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                        <TableCell sx={{ color: 'text.primary', border: 'none', py: 1.5 }}>{shortcut.action}</TableCell>
                        <TableCell sx={{ border: 'none', py: 1.5 }}>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {shortcut.keys.map((key, keyIndex) => (
                              <React.Fragment key={keyIndex}>
                                <Chip
                                  label={key}
                                  size="small"
                                  sx={{
                                    bgcolor: 'action.selected',
                                    color: 'text.primary',
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    height: 22,
                                    '& .MuiChip-label': { px: 1 },
                                  }}
                                />
                                {keyIndex < shortcut.keys.length - 1 && (
                                  <Typography variant="body2" sx={{ color: 'text.secondary', alignSelf: 'center' }}>+</Typography>
                                )}
                              </React.Fragment>
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))}
        </>
      )}

      {!enabled && (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'action.hover' }}>
          <Typography variant="body2" color="text.secondary">
            Keyboard shortcuts are disabled. Enable them to see available shortcuts.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default KeyboardShortcuts;
